"use client";

import { useEffect, useRef, useState } from "react";
import dynamic from "next/dynamic";
const FloatingLines = dynamic(() => import("@/components/FloatingLines"), { ssr: false });
import Link from "next/link";
import BookDemoModal from "@/components/BookDemoModal";

const LINES_GRADIENT = ["#7a431d", "#b87333", "#f2c79f", "#b87333", "#7a431d"];
const ENABLED_WAVES: Array<"top" | "middle" | "bottom"> = ["top", "middle", "bottom"];

export default function Hero() {
  const [demoOpen, setDemoOpen] = useState(false);
  const [videoMuted, setVideoMuted] = useState(true);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [showMotion, setShowMotion] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const media = window.matchMedia("(min-width: 1024px) and (prefers-reduced-motion: no-preference)");
    const timer = window.setTimeout(() => setShowMotion(media.matches), 1200);
    return () => window.clearTimeout(timer);
  }, []);

  const toggleVideo = async () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) await video.play();
    else video.pause();
  };

  return (
    <section className="relative isolate overflow-hidden min-h-[100dvh] flex items-center justify-center py-20">
      {showMotion && (
        <div className="pointer-events-none absolute inset-0 opacity-40 mix-blend-screen" aria-hidden="true">
          <FloatingLines
            linesGradient={LINES_GRADIENT}
            enabledWaves={ENABLED_WAVES}
            lineCount={3}
            lineDistance={8}
            animationSpeed={1}
            bendRadius={6}
            bendStrength={-0.5}
            interactive={false}
            parallax={false}
          />
        </div>
      )}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(184,115,51,0.12),transparent_55%)]"
      />

      <div className="relative mx-auto max-w-5xl px-5 text-center sm:px-6 lg:px-8">
        <h1 className="mt-6 font-display text-[2.2rem] font-extrabold leading-[1.05] tracking-tight text-white sm:text-[3rem] md:text-[3.5rem] lg:text-[3.25rem] xl:text-[3.6rem]">
          Referral software for engineering teams,{" "}
          <span className="text-gradient-copper inline-block pb-1 drop-shadow-[0_0_18px_rgba(184,115,51,0.35)]">
            live in an afternoon.
          </span>
        </h1>

        <p className="animate-blur-reveal delay-200 mx-auto mt-6 max-w-2xl text-base leading-relaxed text-zinc-400 sm:text-[17px] md:text-lg">
          Launch native referral programs across web, iOS, and Android with first-party SDKs, a REST API, verified conversion integrations, and retryable reward webhooks.
        </p>

        <div className="animate-blur-reveal delay-300 mx-auto mt-9 flex flex-col items-center justify-center gap-4 sm:flex-row">
          <Link
            href="https://app.growthrail.dev/auth"
            className="group relative inline-flex w-full items-center justify-center gap-2 overflow-hidden rounded-full border border-copper-300/30 bg-gradient-to-b from-copper-400 to-copper-600 px-8 py-3.5 text-sm font-semibold text-white shadow-[0_0_40px_rgba(184,115,51,0.3)] transition-all hover:-translate-y-0.5 hover:from-copper-300 hover:to-copper-500 hover:shadow-[0_0_60px_rgba(184,115,51,0.5)] sm:w-auto"
          >
            <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
            Start for free
            <svg className="h-4 w-4 transition-transform group-hover:translate-x-1" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path d="M5 10h10m0 0l-4-4m4 4l-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" fill="none" />
            </svg>
          </Link>
          <button
            type="button"
            onClick={() => setDemoOpen(true)}
            className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-white/10 bg-white/5 px-8 py-3.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-white/10 sm:w-auto"
          >
            <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5h8v8H6V7z" />
            </svg>
            Book a demo
          </button>
        </div>

        <div className="animate-blur-reveal delay-400 relative mx-auto mt-12 w-full max-w-5xl">
          <div
            aria-hidden="true"
            className="absolute -inset-px rounded-[1.6rem] bg-gradient-to-b from-copper-300/50 via-copper-500/10 to-transparent blur-[1px]"
          />
          <div className="relative overflow-hidden rounded-[1.5rem] border border-white/[0.14] bg-[#0b0a09] p-1 shadow-[0_28px_90px_rgba(0,0,0,0.65),0_0_60px_rgba(184,115,51,0.12)]">
            <div className="relative aspect-video overflow-hidden rounded-[1.25rem] border border-white/[0.06] bg-black">
              <video
                ref={videoRef}
                className="h-full w-full object-cover"
                muted={videoMuted}
                loop
                playsInline
                preload="none"
                poster="/videos/growthrail-launch-poster.webp"
                aria-label="GrowthRail product launch video"
                onPlay={() => setVideoPlaying(true)}
                onPause={() => setVideoPlaying(false)}
              >
                <source src="/videos/growthrail-launch.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              {!videoPlaying && (
                <button
                  type="button"
                  onClick={() => void toggleVideo()}
                  className="absolute inset-0 m-auto inline-flex h-14 w-fit items-center gap-2 rounded-full border border-white/20 bg-black/75 px-6 text-sm font-semibold text-white shadow-xl backdrop-blur-md transition hover:border-copper-300/50 hover:bg-black/90 focus-visible:ring-2 focus-visible:ring-copper-300/70"
                  aria-label="Play the GrowthRail product tour"
                >
                  <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path d="M6 4.7a1 1 0 0 1 1.53-.85l8.2 5.3a1 1 0 0 1 0 1.7l-8.2 5.3A1 1 0 0 1 6 15.3V4.7Z" />
                  </svg>
                  Play product tour
                </button>
              )}
              <button
                type="button"
                onClick={() => setVideoMuted((muted) => !muted)}
                aria-label={videoMuted ? "Unmute launch video" : "Mute launch video"}
                aria-pressed={!videoMuted}
                className="absolute bottom-3 right-3 inline-flex h-12 w-12 cursor-pointer items-center justify-center rounded-full border border-white/15 bg-black/60 text-white shadow-lg backdrop-blur-md transition-all hover:-translate-y-0.5 hover:border-copper-300/40 hover:bg-[#1a120d] hover:text-copper-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-copper-300/70 sm:bottom-4 sm:right-4"
              >
                {videoMuted ? (
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M11 5 6 9H3v6h3l5 4V5Z" />
                    <path d="m23 9-6 6" />
                    <path d="m17 9 6 6" />
                  </svg>
                ) : (
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M11 5 6 9H3v6h3l5 4V5Z" />
                    <path d="M15.5 8.5a5 5 0 0 1 0 7" />
                    <path d="M19 5a10 10 0 0 1 0 14" />
                  </svg>
                )}
              </button>
            </div>
          </div>
          <div
            aria-hidden="true"
            className="absolute -bottom-12 left-1/2 h-20 w-3/4 -translate-x-1/2 rounded-full bg-copper-500/10 blur-3xl"
          />
        </div>
      </div>

      <BookDemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
    </section>
  );
}
